import React, { useEffect } from "react";
import {
  Form,
  Input,
  Select,
  Checkbox,
  Button,
  Card,
  Typography,
  message,
} from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import AppModal from "../../modals/AppModal";

type FieldType = "text" | "number" | "date" | "option_list";
type DescriptorSetSlim = { id: string; name: string };
type Mode = "add" | "edit";

type FormShape = {
  name: string;
  type: FieldType;
  is_dynamic: boolean;
  options?: string[];
  description?: string;
};

type Props = {
  open: boolean;
  loading?: boolean;
  onCancel: () => void;
  onAdd?: (values: FormShape) => void;
  onEdit?: (values: FormShape) => void;
  mode?: Mode;
  initialValues?: Partial<FormShape>;
  selectedSet: DescriptorSetSlim | null;
  className?: string;
};

const AddFieldModal: React.FC<Props> = ({
  open,
  loading,
  onCancel,
  onAdd,
  onEdit,
  mode = "add",
  initialValues,
  selectedSet,
  className,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (initialValues) {
      form.setFieldsValue({
        name: initialValues.name ?? "",
        type: initialValues.type ?? undefined,
        is_dynamic: !!initialValues.is_dynamic,
        description: initialValues.description ?? "",
        options:
          initialValues.type === "option_list"
            ? initialValues.options && initialValues.options.length > 0
              ? initialValues.options
              : [""]
            : undefined,
      });
    } else {
      form.setFieldsValue({ options: [""] });
    }
  }, [open, initialValues, form]);

  const handleSubmit = async () => {
    const v = await form.validateFields();

    let options: string[] = [];
    const isOptionList = v.type === "option_list";

    if (isOptionList) {
      options = (v.options ?? []).map((s: string) => s.trim()).filter(Boolean);
      if (!options.length) {
        message.error("Please add at least one option.");
        return;
      }
    }

    const payload: FormShape = {
      name: v.name.trim(),
      type: v.type as FieldType,
      is_dynamic: !!v.is_dynamic,
      description: v.description?.trim() || undefined,
      ...(isOptionList ? { options } : {}), // only include options if type is option_list
    };

    if (mode === "edit" && onEdit) return onEdit(payload);
    if (mode === "add" && onAdd) return onAdd(payload);
  };

  return (
    <AppModal
      open={open}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      title={`${mode === "edit" ? "Edit" : "Add"} field${
        selectedSet
          ? ` ${mode === "add" ? "to: " : "in: "}${selectedSet.name}`
          : ""
      }`}
      width={700}
      footer={
        <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
          <Button className="cancel-button" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            loading={loading}
            onClick={handleSubmit}
            disabled={!selectedSet}
            className="add-button"
          >
            {mode === "edit" ? "Save changes" : "Add"}
          </Button>
        </div>
      }
      className={className}
    >
      {!selectedSet && (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Select a descriptor set first.
        </Typography.Paragraph>
      )}

      <Form form={form} layout="vertical">
        <Form.Item
          label="Title"
          name="name"
          rules={[{ required: true, message: "Title is required" }]}
        >
          <Input placeholder="Title" />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          rules={[
            { required: true, message: "Description is required" },
            { max: 100, message: "Max 100 characters" },
          ]}
        >
          <Input.TextArea
            placeholder="Autosize height based on content lines"
            showCount
            maxLength={100}
            autoSize={{ minRows: 2, maxRows: 6 }}
          />
        </Form.Item>

        <Form.Item
          label="Field type"
          name="type"
          rules={[{ required: true, message: "Type is required" }]}
        >
          <Select placeholder="Select an option">
            <Select.Option value="text">Text</Select.Option>
            <Select.Option value="number">Number</Select.Option>
            <Select.Option value="date">Date</Select.Option>
            <Select.Option value="option_list">Option List</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item name="is_dynamic" valuePropName="checked" hidden>
          <Checkbox>Dynamic Field</Checkbox>
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.type !== cur.type}>
          {({ getFieldValue }) =>
            getFieldValue("type") === "option_list" && (
              <Card
                title="Field Options"
                style={{
                  background: "#FDF8FF",
                  border: "2px solid #F7E3FF",
                  boxShadow: "none",
                }}
                headStyle={{ fontWeight: 700 }}
              >
                <Typography.Text style={{ fontWeight: 600 }}>
                  * Add options
                </Typography.Text>

                <Form.List name="options">
                  {(fields, { add, remove }) => (
                    <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
                      {fields.map((field, index) => {
                        const canRemove = fields.length > 1;
                        return (
                          <div
                            key={field.key}
                            style={{
                              display: "flex",
                              gap: 8,
                              alignItems: "center",
                            }}
                          >
                            <Form.Item
                              {...field}
                              style={{ flex: 1, marginBottom: 0 }}
                              rules={[
                                { required: true, message: "Option required" },
                              ]}
                            >
                              <Input placeholder="Option" />
                            </Form.Item>

                            <Button
                              shape="circle"
                              size="small"
                              onClick={() => canRemove && remove(field.name)}
                              disabled={!canRemove}
                              icon={<MinusOutlined />}
                            />
                            <Button
                              shape="circle"
                              size="small"
                              onClick={() => add("", index + 1)}
                              icon={<PlusOutlined />}
                            />
                          </div>
                        );
                      })}
                      <div>
                        <Button onClick={() => add()}>+ Add option</Button>
                      </div>
                    </div>
                  )}
                </Form.List>
              </Card>
            )
          }
        </Form.Item>
      </Form>
    </AppModal>
  );
};

export default AddFieldModal;
